import React from 'react';
import SEO from '../common/SEO';
import LeadForm from '../LeadForm';
import { Timer, Laptop, Target, ChartBar } from '@phosphor-icons/react';
import {
  trackEvent, container, sectionLight, sectionWhite,
  ROICalculator, LearnDoSection, BeforeAfterSection, AgendaSection,
  DeliverablesSection, QualificationSection, GuaranteeSection,
  TrainerSection, ConfidentialityNote, FAQSection, FinalCTASection
} from './TrainingSections';

/**
 * Shared layout for the Enterprise AI Upskilling pages.
 * All copy comes from src/config/trainingContent.js — this file holds no
 * product-specific wording.
 */

const badgeIcons = [
  (c) => <Timer size={28} color={c} weight="duotone" />,
  (c) => <Laptop size={28} color={c} weight="duotone" />,
  (c) => <Target size={28} color={c} weight="duotone" />,
  (c) => <ChartBar size={28} color={c} weight="duotone" />
];

export default function TrainingPage({ seo, content }) {
  const c = content;

  return (
    <>
      <SEO title={seo.title} description={seo.description} pathname={c.page} />

      <style>{`
        @media (max-width: 1024px) {
          .hero-h1 { font-size: 2.8rem !important; }
          .badge-row { flex-wrap: wrap !important; gap: 10px !important; }
          .cmp-flex { flex-direction: column !important; }
        }
        @media (max-width: 768px) {
          .hero-h1 { font-size: 2.1rem !important; }
          .badge-row { flex-direction: column !important; align-items: stretch !important; }
          .badge-row > div { width: 100% !important; }
          .hero-bg { background-position: right center !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-bg video { display: none; }
        }
      `}</style>

      {/* Hero */}
      <section
        className="hero-bg"
        style={{
          paddingTop: '120px', paddingBottom: '120px', minHeight: '680px',
          display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden',
          background: '#0f172a',
          backgroundImage: c.hero.video ? 'none' : `url(${c.hero.image})`,
          backgroundSize: 'cover', backgroundPosition: '100% 40%'
        }}
      >
        {c.hero.video && (
          <video
            autoPlay
            loop
            muted
            playsInline
            aria-hidden="true"
            tabIndex={-1}
            poster={c.hero.image}
            style={{
              position: 'absolute', top: '50%', left: '50%', width: '100%', height: '100%',
              objectFit: 'cover', transform: 'translate(-50%, -50%)', zIndex: 0, opacity: 0.9,
            }}
          >
            <source src={c.hero.video} type="video/mp4" />
          </video>
        )}
        {/* Bright footage under white copy needs a heavier scrim than a dark still does. */}
        <div style={{ position: 'absolute', inset: 0, zIndex: 1, background: c.hero.video
          ? 'linear-gradient(to right, rgba(15, 23, 42, 0.96) 0%, rgba(15, 23, 42, 0.90) 38%, rgba(15, 23, 42, 0.45) 68%, rgba(15, 23, 42, 0.12) 100%)'
          : 'linear-gradient(to right, rgba(15, 23, 42, 0.98) 0%, rgba(15, 23, 42, 0.95) 45%, rgba(15, 23, 42, 0.4) 75%, transparent 100%)' }} />

        <div style={{ ...container, position: 'relative', zIndex: 2 }}>
          <div style={{ maxWidth: '760px' }}>
            <span style={{ color: '#bae6fd', fontWeight: 800, fontSize: '0.9rem', textTransform: 'uppercase', letterSpacing: '0.15em', display: 'block', marginBottom: '16px' }}>
              {c.hero.eyebrow}
            </span>

            <h1 className="hero-h1" style={{ fontSize: '3.4rem', fontWeight: 900, color: '#ffffff', letterSpacing: '-0.03em', margin: '0 0 24px', lineHeight: 1.12 }}>
              {c.hero.titleLead}
              <span style={{ color: '#f87171' }}>{c.hero.titleAccent}</span>
              {c.hero.titleTail}
            </h1>

            <p style={{ fontSize: '1.22rem', color: '#ffffff', lineHeight: 1.7, marginBottom: '30px', fontWeight: 500 }}>
              {c.hero.sub}
            </p>

            <div className="badge-row" style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '32px' }}>
              {c.hero.badges.map((b, i) => (
                <div key={b.title} style={{ background: 'rgba(15, 23, 42, 0.85)', border: `1px solid ${b.color}55`, padding: '12px 16px', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: '10px', backdropFilter: 'blur(8px)', flex: '0 1 auto' }}>
                  {badgeIcons[i % badgeIcons.length](b.color)}
                  <div>
                    <div style={{ color: b.color, fontWeight: 900, fontSize: '0.92rem', lineHeight: 1.15, whiteSpace: 'nowrap' }}>{b.title}</div>
                    <div style={{ color: '#e2e8f0', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', marginTop: '4px', whiteSpace: 'nowrap' }}>{b.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            <a
              href="#lead-form"
              onClick={() => trackEvent('cta_click', { button: c.hero.gtag, page: c.page })}
              style={{
                background: 'linear-gradient(135deg, #0071e3, #2563eb)', color: '#ffffff',
                padding: '19px 44px', borderRadius: '12px', fontWeight: 900, textDecoration: 'none',
                fontSize: '1.2rem', boxShadow: '0 15px 35px -5px rgba(37, 99, 235, 0.6)', display: 'inline-block'
              }}
            >
              {c.hero.ctaLabel}
            </a>

            <div style={{ marginTop: '20px', color: '#ffffff', fontSize: '1.02rem', fontWeight: 700 }}>
              ✓ We come to your office. Private session, your own data.
            </div>
            <div style={{ marginTop: '10px', color: '#cbd5e1', fontSize: '0.95rem', lineHeight: 1.6, maxWidth: '620px' }}>
              {c.hero.security}
            </div>
          </div>
        </div>
      </section>

      <ConfidentialityNote text={c.confidentiality} />

      {/* Generic vs specialised + ROI */}
      <section style={sectionWhite}>
        <div style={container}>
          <div style={{ textAlign: 'center', maxWidth: '820px', margin: '0 auto' }}>
            <h2 style={{ fontSize: '2.4rem', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.03em', marginBottom: '20px', lineHeight: 1.2 }}>
              {c.comparison.heading}
            </h2>
            <p style={{ color: '#475569', fontSize: '1.12rem', lineHeight: 1.8, marginBottom: '36px' }}>
              {c.comparison.lead}
            </p>
          </div>

          <div className="cmp-flex" style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', alignItems: 'stretch' }}>
            <div style={{ flex: 1, minWidth: '300px', background: 'white', borderRadius: '24px', padding: '32px', border: '1px solid #f1f5f9', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <div style={{ color: '#ef4444', fontWeight: 700, fontSize: '1.15rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</div>
                {c.comparison.genericTitle}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {c.comparison.generic.map((it) => (
                  <li key={it.strong} style={{ paddingLeft: '24px', position: 'relative', color: '#475569', fontSize: '1rem', lineHeight: 1.7 }}>
                    <span style={{ position: 'absolute', left: 0, color: '#ef4444' }}>•</span>
                    <strong>{it.strong}:</strong> {it.text}
                  </li>
                ))}
              </ul>
            </div>

            <div style={{ flex: 1, minWidth: '300px', background: 'linear-gradient(180deg, #f8fafc 0%, white 100%)', borderRadius: '24px', padding: '32px', border: '2px solid #0ea5e9', boxShadow: '0 20px 25px -5px rgba(14,165,233,0.1)', position: 'relative' }}>
              <div style={{ position: 'absolute', top: '-12px', right: '32px', background: '#0ea5e9', color: 'white', padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                The Eagentix Way
              </div>
              <div style={{ color: '#0ea5e9', fontWeight: 800, fontSize: '1.15rem', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e0f2fe', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✓</div>
                {c.comparison.specialisedTitle}
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {c.comparison.specialised.map((it) => (
                  <li key={it.strong} style={{ paddingLeft: '24px', position: 'relative', color: '#0f172a', fontSize: '1rem', lineHeight: 1.7, fontWeight: 500 }}>
                    <span style={{ position: 'absolute', left: 0, color: '#0ea5e9' }}>✓</span>
                    <strong>{it.strong}:</strong> {it.text}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <ROICalculator productName={c.roi.productName} defaultSeatCost={c.roi.defaultSeatCost} page={c.page} />

          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <a
              href="#lead-form"
              onClick={() => trackEvent('cta_click', { button: 'Post-ROI Request Training', page: c.page })}
              style={{ background: '#0071e3', color: 'white', padding: '17px 38px', borderRadius: '12px', fontWeight: 800, textDecoration: 'none', fontSize: '1.08rem', display: 'inline-block', boxShadow: '0 12px 28px rgba(0,113,227,0.3)' }}
            >
              Request a Private Onsite Session →
            </a>
          </div>
        </div>
      </section>

      <LearnDoSection {...c.learnDo} />
      <BeforeAfterSection {...c.beforeAfter} />
      <AgendaSection {...c.agenda} />
      <DeliverablesSection {...c.deliverables} />
      <QualificationSection {...c.qualification} />
      <GuaranteeSection {...c.guarantee} />
      <TrainerSection {...c.trainer} page={c.page} />

      <section style={{ ...sectionLight, paddingTop: '0', paddingBottom: '60px', textAlign: 'center' }}>
        <div style={container}>
          <a
            href="#lead-form"
            onClick={() => trackEvent('cta_click', { button: 'Post-Trainer Request Training', page: c.page })}
            style={{ background: '#0071e3', color: 'white', padding: '17px 38px', borderRadius: '12px', fontWeight: 800, textDecoration: 'none', fontSize: '1.08rem', display: 'inline-block', boxShadow: '0 12px 28px rgba(0,113,227,0.3)' }}
          >
            Request a Private Onsite Session →
          </a>
        </div>
      </section>

      <FAQSection heading="Questions before you book" faqs={c.faqs} page={c.page} />

      <FinalCTASection {...c.finalCta}>
        <LeadForm page={c.page} showTeamSize {...c.leadForm} />
      </FinalCTASection>
    </>
  );
}
